import type { ReactNode, JSX } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  CircleCheckBigIcon,
  XIcon,
  EyeIcon,
  Flex,
  Select,
  Text,
  UserRoundCheckIcon,
} from '@buffer-mono/popcorn'

import type { AccessLevel, ProductKey } from '../../types'

import styles from './ChannelPermissions.module.css'

/**
 * Split gating the rollout of assigning the publish `viewOnly` tier
 * (CORE-6398). It does not gate the read-only UI states themselves — those
 * activate for any account that already holds the permission.
 */
export const PUBLISH_VIEW_ONLY_SPLIT = 'publish-view-only'

interface AccessLevelOption {
  value: AccessLevel
  label: string
  icon: ReactNode
  /**
   * Color class applied to the option (icon + label). Set via CSS rather than
   * the icon `color` prop so the color survives Radix mirroring the selected
   * item into the closed trigger — the prop-based color was being dropped
   * there, leaving the trigger icons grey.
   */
  colorClass: string
}

/**
 * Per-product access-level options, mapping the wire enum `ChannelAccessLevel`
 * to product-specific labels. Only the publish column offers
 * `requiresApproval` — Community has no "requires approval" level.
 *
 * @see graphql/src/channel/channel.graphql for the `ChannelAccessLevel` values.
 */
export const ACCESS_LEVEL_OPTIONS: Record<ProductKey, AccessLevelOption[]> = {
  publish: [
    {
      value: 'fullPosting',
      label: 'Full Access',
      icon: <CircleCheckBigIcon size="small" />,
      colorClass: styles.levelSuccess,
    },
    {
      value: 'requiresApproval',
      label: 'Needs Approval',
      icon: <UserRoundCheckIcon size="small" />,
      colorClass: styles.levelWarning,
    },
    {
      value: 'viewOnly',
      label: 'View Only',
      icon: <EyeIcon size="small" />,
      colorClass: styles.levelMuted,
    },
    {
      value: 'none',
      label: 'No Access',
      icon: <XIcon size="small" />,
      colorClass: styles.levelCritical,
    },
  ],
  comments: [
    {
      value: 'fullPosting',
      label: 'Full Access',
      icon: <CircleCheckBigIcon size="small" />,
      colorClass: styles.levelSuccess,
    },
    {
      value: 'viewOnly',
      label: 'View Only',
      icon: <EyeIcon size="small" />,
      colorClass: styles.levelMuted,
    },
    {
      value: 'none',
      label: 'No Access',
      icon: <XIcon size="small" />,
      colorClass: styles.levelCritical,
    },
  ],
}

function isAccessLevel(value: string): value is AccessLevel {
  return (
    value === 'fullPosting' ||
    value === 'requiresApproval' ||
    value === 'viewOnly' ||
    value === 'none'
  )
}

interface AccessLevelSelectProps {
  product: ProductKey
  value: AccessLevel
  onChange: (level: AccessLevel) => void
  /** Accessible name — every select must be uniquely addressable. */
  'aria-label': string
  /**
   * Fill the container width instead of the fixed default. Used by the
   * collapsed "applies to all" pair (two side-by-side columns) so each select
   * tracks its column rather than the 190px default sized for the legacy row.
   */
  fullWidth?: boolean
  /** Disable the select (e.g. the channel owner's row can't be edited). */
  disabled?: boolean
}

/**
 * Reusable per-product access-level dropdown. Renders an icon + label per
 * option; the chosen icon + label is mirrored into the trigger (Radix
 * `ItemText`). Shared by the collapsed "applies to all" pair and the
 * per-channel table rows so the option set lives in one place.
 */
export const AccessLevelSelect = ({
  product,
  value,
  onChange,
  'aria-label': ariaLabel,
  fullWidth = false,
  disabled,
}: AccessLevelSelectProps): JSX.Element => {
  const { isEnabled: publishViewOnlyEnabled } = useSplitEnabled(
    PUBLISH_VIEW_ONLY_SPLIT,
  )

  // While the rollout split is off, hide the publish `viewOnly` option unless
  // it is the current value — an existing grant must still display and be
  // changeable away from.
  const options = ACCESS_LEVEL_OPTIONS[product].filter(
    (option) =>
      option.value !== 'viewOnly' ||
      product !== 'publish' ||
      publishViewOnlyEnabled ||
      value === 'viewOnly',
  )

  return (
    <Select
      value={value}
      onValueChange={(next): void => {
        if (isAccessLevel(next)) onChange(next)
      }}
      disabled={disabled}
    >
      <Select.Trigger
        className={
          fullWidth
            ? `${styles.accessSelect} ${styles.fullWidthSelect}`
            : styles.accessSelect
        }
        size="medium"
        aria-label={ariaLabel}
      />
      <Select.Content align="end">
        {options.map((option) => (
          <Select.Item key={option.value} value={option.value}>
            <Flex align="center" gap="2xs" className={styles.optionLabel}>
              <span className={`${styles.optionIcon} ${option.colorClass}`}>
                {option.icon}
              </span>
              <Text className={styles.optionText}>{option.label}</Text>
            </Flex>
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}
